import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    Box,
    IconButton,
    Popover,
    Radio,
    RadioGroup,
    Checkbox,
    FormControlLabel,
    Divider,
    Typography,
    Tooltip,
} from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { CHAT_LLM_PROVIDERS } from '../../hooks/useChatLlmProvider';
import { hasSources, resolveSelection, toggleId } from './chatSources';

// Gear button + popover holding everything that shapes the next Ask Prompta
// answer. Two sections:
//
//   AI model — which LLM answers (Claude / OpenAI), owned by the parent via
//              provider/onChange.
//   Sources  — which of the project's data the answer is drawn from, owned by
//              the parent via sourcesValue/onSourcesChange.
//
// Sources lives here rather than in a button of its own because it is a
// setting, not an action: one gear beside the input is the whole surface. The
// section is omitted entirely when the project has nothing to offer, so a chat
// whose options have not arrived (or whose backend predates the endpoint) sees
// exactly the popover it saw before.
//
// A source group renders only when the endpoint returned items for it — an
// empty Documents list means no Documents group, not an empty one. Everything
// is checked by default (`sourcesValue === null`). There is no risk option: the
// endpoint reports `risk_model`, but the chat carries no risk grounding for a
// flag to gate yet.
const ChatSettingsButton = ({
    provider,
    onChange,
    disabled = false,
    sourcesAvailable = null,
    sourcesValue = null,
    onSourcesChange,
}) => {
    const intl = useIntl();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const label = intl.formatMessage({
        id: 'chatSettings.title',
        defaultMessage: 'Chat settings',
    });

    const showSources = hasSources(sourcesAvailable) && Boolean(onSourcesChange);
    const selection = resolveSelection(sourcesValue, sourcesAvailable);
    const waves = sourcesAvailable?.waves || [];
    const documents = sourcesAvailable?.documents || [];

    const sourceRow = (key, text, checked, onToggle) => (
        <FormControlLabel
            key={key}
            control={<Checkbox size="small" checked={checked} onChange={onToggle} />}
            label={
                <Box component="span" sx={{ fontSize: '0.875rem' }}>
                    {text}
                </Box>
            }
            sx={{ display: 'flex', ml: 0 }}
        />
    );

    const sourceGroup = (headerId, headerDefault, rows) =>
        rows.length === 0 ? null : (
            <Box sx={{ mb: 1, '&:last-of-type': { mb: 0 } }}>
                <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: 'text.disabled', display: 'block', mb: 0.25 }}
                >
                    <FormattedMessage id={headerId} defaultMessage={headerDefault} />
                </Typography>
                {rows}
            </Box>
        );

    return (
        <>
            <Tooltip title={label}>
                <span>
                    <IconButton
                        size="small"
                        aria-label={label}
                        disabled={disabled}
                        onClick={(e) => setAnchorEl(e.currentTarget)}
                        sx={{
                            width: 32,
                            height: 32,
                            color: 'text.secondary',
                            '&:hover': { bgcolor: 'action.hover', color: 'primary.main' },
                        }}
                    >
                        <SettingsOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </span>
            </Tooltip>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                PaperProps={{
                    sx: { borderRadius: 2, p: 1.5, minWidth: 240, maxHeight: 420, boxShadow: 3 },
                }}
            >
                <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}
                >
                    <FormattedMessage id="chatSettings.ai_model" defaultMessage="AI model" />
                </Typography>
                <RadioGroup
                    value={provider}
                    onChange={(e) => onChange(e.target.value)}
                >
                    <FormControlLabel
                        value={CHAT_LLM_PROVIDERS.ANTHROPIC}
                        control={<Radio size="small" />}
                        label={
                            <Box component="span" sx={{ fontSize: '0.875rem' }}>
                                <FormattedMessage
                                    id="chatSettings.model_claude"
                                    defaultMessage="Claude (default)"
                                />
                            </Box>
                        }
                    />
                    <FormControlLabel
                        value={CHAT_LLM_PROVIDERS.OPENAI}
                        control={<Radio size="small" />}
                        label={
                            <Box component="span" sx={{ fontSize: '0.875rem' }}>
                                <FormattedMessage
                                    id="chatSettings.model_openai"
                                    defaultMessage="OpenAI"
                                />
                            </Box>
                        }
                    />
                </RadioGroup>

                {showSources && (
                    <>
                        <Divider sx={{ my: 1.25 }} />
                        <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}
                        >
                            <FormattedMessage
                                id="chatSources.section_title"
                                defaultMessage="Sources"
                            />
                        </Typography>

                        {sourceGroup(
                            'chatSources.waves',
                            'Waves',
                            waves.map((wave) =>
                                sourceRow(
                                    `wave-${wave.id}`,
                                    wave.label,
                                    selection.waveIds.includes(wave.id),
                                    () =>
                                        onSourcesChange({
                                            ...selection,
                                            waveIds: toggleId(selection.waveIds, wave.id),
                                        })
                                )
                            )
                        )}

                        {sourceGroup(
                            'chatSources.documents',
                            'Documents',
                            documents.map((doc) =>
                                sourceRow(
                                    `document-${doc.id}`,
                                    doc.name,
                                    selection.documentIds.includes(doc.id),
                                    () =>
                                        onSourcesChange({
                                            ...selection,
                                            documentIds: toggleId(selection.documentIds, doc.id),
                                        })
                                )
                            )
                        )}

                        {sourcesAvailable?.impact_assessment &&
                            sourceGroup('chatSources.impact_assessment', 'Impact assessment', [
                                sourceRow(
                                    'impacts',
                                    intl.formatMessage({
                                        id: 'chatSources.impact_register',
                                        defaultMessage: 'Impact register',
                                    }),
                                    selection.includeImpacts,
                                    () =>
                                        onSourcesChange({
                                            ...selection,
                                            includeImpacts: !selection.includeImpacts,
                                        })
                                ),
                            ])}
                    </>
                )}
            </Popover>
        </>
    );
};

export default ChatSettingsButton;
